import {
  Button,
  Divider,
  Flex,
  Group,
  InputLabel,
  Paper,
  Select,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconEdit, IconEye, IconTrash, IconX } from "@tabler/icons-react";
import { useLanguage } from "../../stores/LanguageStore";
import UsersSelector from "../UserSelector/UsersSelector";
import CommunitySelector from "./CommunitySelector";
import { Controller, useWatch } from "react-hook-form";
import { useCallback } from "react";

const SharingItem = ({
  index,
  setValue,
  update,
  getValues,
  control,
  handleDelete,
  readOnly,
  disablePermissions,
  disableDelete,
  size = "sm",
  allowResellerCustomers,
}) => {
  const lang = useLanguage((s) => s.language);

  const values = getValues(`sharing.${index}`);
  useWatch({
    control,
    name: [
      `sharing.${index}.write_permission`,
      `sharing.${index}.delete_permission`,
    ],
  });

  const handleTypeChange = (v) => {
    update(index, { ...values, sharing_type_to: v, sharing_targets: [] });
  };

  const handleTargetsChange = useCallback(
    (change) => {
      update(index, {
        ...values,
        sharing_targets: change.map((x) => ({
          id: x.id,
          option_name:
            x?.option_name || x?.name || `${x.first_name} ${x.last_name}`,
        })),
      });
    },
    [index, update, values]
  );

  const getTargetsComponents = useCallback(() => {
    let sharing_type_to = values?.sharing_type_to || "User";
    let sharing_targets = values?.sharing_targets || [];

    if (sharing_type_to === "User") {
      return (
        <UsersSelector
          values={sharing_targets}
          onChange={handleTargetsChange}
          placeholder={lang.components.sharing.values.user_placeholder}
          inputStyles={{
            borderBottomLeftRadius: 0,
            borderTopLeftRadius: 0,
          }}
          size={size}
        />
      );
    } else {
      return (
        <CommunitySelector
          values={sharing_targets}
          onChange={handleTargetsChange}
          placeholder={
            lang.components.sharing.values[
              sharing_type_to === "Team"
                ? "team_placeholder"
                : sharing_type_to === "Organization"
                ? "org_placeholder"
                : "reseller_placeholder"
            ]
          }
          type={sharing_type_to}
          inputStyles={{
            borderBottomLeftRadius: 0,
            borderTopLeftRadius: 0,
          }}
          size={size}
          allowResellerCustomers={allowResellerCustomers}
        />
      );
    }
  }, [
    handleTargetsChange,
    lang.components.sharing.values,
    size,
    values?.sharing_targets,
    values?.sharing_type_to,
    allowResellerCustomers,
  ]);

  return (
    <>
      <Paper bg={"none"} mb={8}>
        <Group align="start" wrap="nowrap">
          <Stack gap={0} w={"100%"}>
            <InputLabel size={size} required mb={2}>
              {lang.components.sharing.values.label}
            </InputLabel>
            <Group wrap="nowrap" gap={0} align="stretch">
              <Controller
                render={({ field }) => (
                  <Select
                    data={[
                      {
                        value: "User",
                        label: lang.components.sharing.types.user,
                      },
                      {
                        value: "Team",
                        label: lang.components.sharing.types.team,
                      },
                      {
                        value: "Organization",
                        label: lang.components.sharing.types.org,
                      },
                      ...(allowResellerCustomers
                        ? [
                            {
                              value: "ResellerCustomers",
                              label: lang.users_list.resellers,
                            },
                          ]
                        : []),
                    ]}
                    placeholder={lang.components.sharing.types.label}
                    w={size === "md" ? 150 : 120}
                    size={size}
                    styles={{
                      wrapper: {
                        height: "100%",
                      },
                      input: {
                        borderRight: 0,
                        borderBottomRightRadius: 0,
                        borderTopRightRadius: 0,
                        height: "100%",
                      },
                    }}
                    {...field}
                    onChange={handleTypeChange}
                  />
                )}
                name={`sharing.${index}.sharing_type_to`}
                control={control}
              />
              <Flex flex={"1 1 auto"} w={0}>
                {getTargetsComponents()}
              </Flex>
            </Group>
          </Stack>
          {!disablePermissions &&
            values?.sharing_type_to !== "ResellerCustomers" && (
              <Stack gap={0} miw={100}>
                <InputLabel size={size} required mb={2}>
                  {lang.components.sharing.permissions.label}
                </InputLabel>
                <Group wrap="nowrap" gap={"xs"} mt={4}>
                  <Tooltip label={lang.components.sharing.permissions.read}>
                    <Button variant="default" px={6} size="xs" disabled>
                      <IconEye size={16} />
                    </Button>
                  </Tooltip>

                  {!readOnly && (
                    <Tooltip label={lang.components.sharing.permissions.write}>
                      <Button
                        variant={values.write_permission ? "filled" : "outline"}
                        px={6}
                        size="xs"
                        onClick={() =>
                          setValue(
                            `sharing.${index}.write_permission`,
                            !values.write_permission
                          )
                        }
                      >
                        <IconEdit size={16} />
                      </Button>
                    </Tooltip>
                  )}

                  {!readOnly && (
                    <Tooltip label={lang.components.sharing.permissions.delete}>
                      <Button
                        variant={
                          values.delete_permission ? "filled" : "outline"
                        }
                        px={6}
                        size="xs"
                        onClick={() =>
                          setValue(
                            `sharing.${index}.delete_permission`,
                            !values.delete_permission
                          )
                        }
                      >
                        <IconTrash size={16} />
                      </Button>
                    </Tooltip>
                  )}
                </Group>
              </Stack>
            )}
        </Group>
        {values?.sharing_type_to === "ResellerCustomers" && (
          <Text size="sm" c={"red"} fw={500} mt={4}>
            {lang.components.sharing.values.reseller_text}
          </Text>
        )}
        {!disableDelete && (
          <Group justify="center">
            <Button
              color="red"
              variant="light"
              onClick={handleDelete}
              size={"compact-xs"}
              leftSection={<IconX size="1rem" />}
              mt={8}
            >
              {lang.global.delete}
            </Button>
          </Group>
        )}
      </Paper>
    </>
  );
};

export default SharingItem;
